import type { ChatTask, ChatTaskStatus } from "@lib/types";
import { Check, Loader2 } from "lucide-react";

interface TaskListCardProps {
  tasks: ChatTask[];
}

function TaskIcon({ status }: { status: ChatTaskStatus }) {
  if (status === "in_progress") {
    return <Loader2 size={16} className="animate-spin text-accent" />;
  }
  if (status === "completed") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-emerald-600 text-white">
        <Check size={12} strokeWidth={3} />
      </span>
    );
  }
  return <span className="block h-4 w-4 rounded-sm border border-text-tertiary/50" />;
}

export function TaskListCard({ tasks }: TaskListCardProps) {
  const valid = tasks.filter((t) => t?.content);
  if (valid.length === 0) return null;

  const completed = valid.filter((t) => t.status === "completed").length;

  return (
    <div className="rounded-lg border border-border bg-surface-primary p-3 text-sm shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Tasks
        </div>
        <span className="text-xs text-text-tertiary">
          {completed}/{valid.length}
        </span>
      </div>
      <ul className="mt-2 space-y-1.5">
        {valid.map((task, idx) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: task list is replaced wholesale on each write
            key={`task-${idx}`}
            className={`flex gap-2 ${
              task.status === "in_progress"
                ? "font-medium text-text-primary"
                : task.status === "completed"
                  ? "text-text-tertiary line-through"
                  : "text-text-secondary"
            }`}
          >
            <span className="mt-0.5">
              <TaskIcon status={task.status} />
            </span>
            <span>{task.content}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
